import { html } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_ATTACHMENT_FILTERING_RULES_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../../ui/gzCheckbox';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzDropdownWebComponent } from '../../../../../../../../../../ui/gzDropdown';
import { toggleDisabled, toggleVisibility } from '../../../../../../../../../../utils/htmlElement.utils';
import { ExchangeContentControlAttachmentFilteringRuleFormValue } from '../../../../../../../utils/uiSettings/exchange/exchange-protection.types';
import {
  getExchangeApplyToRuleFormOptions,
  getExchangeContentControlActionAttachmentRuleFormOptions,
} from '../../../../../../../utils/uiSettings/exchange/exchange-protection.values';
import {
  ExchangeRuleScopeApplyToType,
  ExchangeRuleScopeEmailSelection,
  ExchangeContentControlAttachmentFilteringRuleSettingsDetectType,
  ExchangeContentControlRuleActionType,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzRadioGroupWebComponent } from '../../../../../../../../../../ui/gzRadio';
import { GzExchangeUserGroupsGridWebComponent } from '../../../../../helpers/gridComponents/userGroupsWithSelection/GzExchangeUserGroupsGridWebComponent';
import { CustomizableValidator } from '../../../../../../../../../../ui/gzForm/validators/customizable.validator';
import { ruleScopeSpecificValidators, validateSettingFilterSelectionGroup } from '../utils/validators';
import _ from 'lodash';
import { FontSizes, FontWeights } from '../../../../../../../../../../types/common/types';
import {
  EXTENSION_VALIDATION_REGEX,
  validateHeaderName,
  validateMultipleEmails,
  validatePathWin,
} from '../../../../../helpers/utils/validators';
import { EmailValidator } from '../../../../../../../../../../ui/gzForm/validators/email.validator';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../../../ui/gzChipsGeneratorInput';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_ATTACHMENT_FILTERING_RULES_FORM)
export class GzExchangeContentControlAttachmentFilteringRulesFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<ExchangeContentControlAttachmentFilteringRuleFormValue> {
  // general section
  @query('#name')
  private ruleName: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#priority')
  private rulePriority: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#active-form-field')
  private activeFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // scope section
  @query('#rule-scope-apply-to-field')
  private ruleScopeApplyToField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-scope-from-field')
  private ruleScopeFromField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-scope-from-specific-field')
  private ruleScopeFromSpecificField: GzFormFieldWebComponent<GzExchangeUserGroupsGridWebComponent>;
  @query('#rule-scope-to-field')
  private ruleScopeToField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-scope-to-specific-field')
  private ruleScopeToSpecificField: GzFormFieldWebComponent<GzExchangeUserGroupsGridWebComponent>;
  @query('#rule-scope-match-all-recipients-field')
  private ruleScopeMatchAllRecipientsField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // settings section
  @query('#rule-settings-detect-by-content-enabled-field')
  private ruleSettingsDetectByContentEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-type-field')
  private ruleSettingsDetectByContentTypeField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-settings-detect-by-content-category-executables-field')
  private ruleSettingsDetectByContentCategoryExecutablesField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-category-images-field')
  private ruleSettingsDetectByContentCategoryImagesField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-category-multimedia-field')
  private ruleSettingsDetectByContentCategoryMultimediaField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-category-archives-field')
  private ruleSettingsDetectByContentCategoryArchivesField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-category-spreadsheets-field')
  private ruleSettingsDetectByContentCategorySpreadsheetsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-category-presentations-field')
  private ruleSettingsDetectByContentCategoryPresentationsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-category-documents-field')
  private ruleSettingsDetectByContentCategoryDocumentsField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-custom-extensions-enabled-field')
  private ruleSettingsDetectByContentCustomExtensionsEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-content-custom-extensions-field')
  private ruleSettingsDetectByContentCustomExtensionsField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#rule-settings-detect-by-content-true-file-type-detection-field')
  private ruleSettingsDetectByContentTrueFileTypeDetectionField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-filename-enabled-field')
  private ruleSettingsDetectByFilenameEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-detect-by-filename-patterns-field')
  private ruleSettingsDetectByFilenamePatternsField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#rule-settings-archive-scan-section')
  private ruleSettingsArchiveScanSection: HTMLElement;
  @query('#rule-settings-archive-scan-enabled-field')
  private ruleSettingsArchiveScanEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-archive-scan-maximum-depth-enabled-field')
  private ruleSettingsArchiveScanMaximumDepthEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#rule-settings-archive-scan-maximum-depth-field')
  private ruleSettingsArchiveScanMaximumDepthField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;

  // action section
  @query('#rule-actions-type-field')
  private ruleActionsTypeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-deliver-to-field')
  private ruleActionsDeliverToField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-modify-subject-field')
  private ruleActionsModifySubjectField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-modify-subject-text-field')
  private ruleActionsModifySubjectTextField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-append-header-field')
  private ruleActionsAppendHeaderField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-append-header-name-field')
  private ruleActionsAppendHeaderNameField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-append-header-value-field')
  private ruleActionsAppendHeaderValueField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-save-mail-to-disk-field')
  private ruleActionsSaveMailToDiskField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-save-mail-to-disk-path-field')
  private ruleActionsSaveMailToDiskPathField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-archive-to-account-field')
  private ruleActionsArchiveToAccountField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-archive-to-account-address-field')
  private ruleActionsArchiveToAccountAddressField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-send-copy-to-field')
  private ruleActionsSendCopyToField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-send-copy-to-address-field')
  private ruleActionsSendCopyToAddressField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rules-actions-notify-section')
  private ruleActionsNotifySection: HTMLElement;
  @query('#rule-actions-notify-recipient-field')
  private ruleActionsNotifyRecipientField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-sender-field')
  private ruleActionsNotifySenderField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-account-field')
  private ruleActionsNotifyAccountField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-account-recipients-field')
  private ruleActionsNotifyAccountRecipientsField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-stop-if-matched-field')
  private ruleActionsStopIfMatchedField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  public constructor(
    private readonly maxPriority: number,
    value?: ExchangeContentControlAttachmentFilteringRuleFormValue
  ) {
    super(value);
    this.maxPriority = maxPriority;
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.ruleName.setError(error);
  }

  protected getConvertedFormValue() {
    const value = _.cloneDeep(this.formGroup.value);

    // set modifiy subject (by default false if missing)
    const modifySubject = _.get(this.formGroup.value, ['actions', 'modifySubject'], false);
    _.set(value, ['actions', 'modifySubject'], modifySubject);

    // set archive to account (by default false if missing)
    const archiveToAccount = _.get(this.formGroup.value, ['actions', 'archiveToAccount'], false);
    _.set(value, ['actions', 'archiveToAccount'], archiveToAccount);

    // set send copy to (by default false if missing)
    const sendCopyTo = _.get(this.formGroup.value, ['actions', 'sendCopyTo'], false);
    _.set(value, ['actions', 'sendCopyTo'], sendCopyTo);

    // set append header (by default false if missing)
    const appendHeader = _.get(this.formGroup.value, ['actions', 'appendHeader'], false);
    _.set(value, ['actions', 'appendHeader'], appendHeader);

    return value;
  }

  protected createFormGroup(): void {
    this.setValidators();

    this.formGroup = FormGroup.build({
      defaultPositionInGrid: this.rulePriority,
      name: this.ruleName,
      active: this.activeFormField,
      scope: {
        applyTo: this.ruleScopeApplyToField,
        from: this.ruleScopeFromField,
        to: this.ruleScopeToField,
        fromSpecific: this.ruleScopeFromSpecificField,
        toSpecific: this.ruleScopeToSpecificField,
        matchAllRecipients: this.ruleScopeMatchAllRecipientsField,
      },
      settings: {
        detectByContentType: {
          enabled: this.ruleSettingsDetectByContentEnabledField,
          detectType: this.ruleSettingsDetectByContentTypeField,
          categoryExecutables: this.ruleSettingsDetectByContentCategoryExecutablesField,
          categoryImages: this.ruleSettingsDetectByContentCategoryImagesField,
          categoryMultimedia: this.ruleSettingsDetectByContentCategoryMultimediaField,
          categoryArchives: this.ruleSettingsDetectByContentCategoryArchivesField,
          categorySpreadsheets: this.ruleSettingsDetectByContentCategorySpreadsheetsField,
          categoryPresentations: this.ruleSettingsDetectByContentCategoryPresentationsField,
          categoryDocuments: this.ruleSettingsDetectByContentCategoryDocumentsField,
          customExtensionsEnabled: this.ruleSettingsDetectByContentCustomExtensionsEnabledField,
          customExtensionsList: this.ruleSettingsDetectByContentCustomExtensionsField,
          trueFileTypeDetection: this.ruleSettingsDetectByContentTrueFileTypeDetectionField,
        },
        detectByFilename: {
          enabled: this.ruleSettingsDetectByFilenameEnabledField,
          patterns: this.ruleSettingsDetectByFilenamePatternsField,
        },
        archiveScan: {
          enabled: this.ruleSettingsArchiveScanEnabledField,
          depthLimitingEnabled: this.ruleSettingsArchiveScanMaximumDepthEnabledField,
          maxDepth: this.ruleSettingsArchiveScanMaximumDepthField,
        },
      },
      actions: {
        type: this.ruleActionsTypeField,
        deliverTo: this.ruleActionsDeliverToField,
        modifySubject: this.ruleActionsModifySubjectField,
        modifySubjectText: this.ruleActionsModifySubjectTextField,
        appendHeader: this.ruleActionsAppendHeaderField,
        headerName: this.ruleActionsAppendHeaderNameField,
        headerValue: this.ruleActionsAppendHeaderValueField,
        saveMailToDisk: this.ruleActionsSaveMailToDiskField,
        saveMailToDiskPath: this.ruleActionsSaveMailToDiskPathField,
        archiveToAccount: this.ruleActionsArchiveToAccountField,
        archiveToAccountAddress: this.ruleActionsArchiveToAccountAddressField,
        sendCopyTo: this.ruleActionsSendCopyToField,
        sendCopyToAddress: this.ruleActionsSendCopyToAddressField,
        stopIfMatched: this.ruleActionsStopIfMatchedField,
        notifyRecipient: this.ruleActionsNotifyRecipientField,
        notifySender: this.ruleActionsNotifySenderField,
        notifyAccount: this.ruleActionsNotifyAccountField,
        notifyAccountRecipients: this.ruleActionsNotifyAccountRecipientsField,
      },
    });
  }

  protected setFormGroupValue() {
    if (!this.value) {
      this.ruleActionsTypeField.value = ExchangeContentControlRuleActionType.DELIVER;
      this.rulePriority.value = 1; // default will be added to the top
      return;
    }

    // for the first change, values from backend are used for secondary actions
    this.ruleActionsTypeField.setAttribute('no-impact', '');

    this.formGroup.value = {
      defaultPositionInGrid: this.value.defaultPositionInGrid,
      name: this.value.name,
      active: this.value.active,
      scope: this.value.scope,
      settings: this.value.settings,
      actions: this.value.actions,
    };
  }

  private setValidators() {
    this.ruleName.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleSettingsDetectByContentCustomExtensionsField.Validators = [
      RequiredValidator(translateFn('Required field')),
    ];
    this.ruleSettingsDetectByFilenamePatternsField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleActionsModifySubjectTextField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleActionsDeliverToField.Validators = [
      RequiredValidator(translateFn('Required field')),
      EmailValidator(translateFn('Invalid email address')),
    ];
    this.ruleActionsAppendHeaderNameField.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => validateHeaderName(value)),
    ];
    this.ruleActionsAppendHeaderValueField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleActionsSaveMailToDiskPathField.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => validatePathWin(value)),
    ];
    this.ruleActionsArchiveToAccountAddressField.Validators = [
      RequiredValidator(translateFn('Required field')),
      EmailValidator(translateFn('Invalid email address')),
    ];
    this.ruleActionsSendCopyToAddressField.Validators = [
      RequiredValidator(translateFn('Required field')),
      EmailValidator(translateFn('Invalid email address')),
    ];
    this.ruleActionsNotifyAccountRecipientsField.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => validateMultipleEmails(value)),
    ];

    // set validators for target scan object group
    const settingFilterSelectionGroupValidator = [
      CustomizableValidator(() =>
        validateSettingFilterSelectionGroup([
          this.ruleSettingsDetectByContentEnabledField.value as boolean,
          this.ruleSettingsDetectByFilenameEnabledField.value as boolean,
        ])
      ),
    ];
    this.ruleSettingsDetectByContentEnabledField.Validators = settingFilterSelectionGroupValidator;
    this.ruleSettingsDetectByFilenameEnabledField.Validators = settingFilterSelectionGroupValidator;

    this.ruleScopeToSpecificField.touched = true;
    this.ruleScopeFromSpecificField.touched = true;
    this.ruleSettingsDetectByContentEnabledField.touched = true;
    this.ruleSettingsDetectByFilenameEnabledField.touched = true;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="rules-form-container">
        <!-- General section -->
        <div id="rules-general-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle">${translateFn('General')}</gz-title>
          <gz-form-field id="name" class="label-mw-250" label="${translateFn('Rule name')}" required>
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the rule name')}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="priority" class="label-mw-250 mt-2" label="${translateFn('Set priority')}" required>
            <gz-input-number
              gz-form-input
              inputWidth="${InputWidthValues.SMALL}"
              min="${1}"
              max="${this.maxPriority}"
            ></gz-input-number>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="active-form-field" class="mt-2">
            <gz-checkbox gz-form-input label="${translateFn('Active')}" checked></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule scope section -->
        <div id="rules-scope-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Rule scope')}</gz-title>
          <gz-form-field id="rule-scope-apply-to-field" label="${translateFn('Apply to (direction)')}">
            <gz-dropdown
              gz-form-input
              .options=${getExchangeApplyToRuleFormOptions()}
              .value=${ExchangeRuleScopeApplyToType.ALL}
            ></gz-dropdown>
          </gz-form-field>

          <gz-labeled-content
            hasFormLabelWidth
            hasColon
            labelFontSize="${FontSizes.HEADING_6}"
            labelFontWeight="${FontWeights.NORMAL}"
            label="${translateFn('Senders')}"
          >
            <gz-form-field id="rule-scope-from-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeRuleScopeEmailSelection.ANY}
                @radioSelectionChanged=${this.handleScopeFromEmailSelectionChange.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Any')}"
                  .value=${ExchangeRuleScopeEmailSelection.ANY}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('Specific')}"
                  .value=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </gz-labeled-content>
          <gz-text
            id="rule-scope-from-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'By default, the rule applies to all senders. To apply different filtering settings and actions for certain sender groups, select {bold}Specific{/bold} and add a custom rule.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-scope-from-specific-field" class="hidden">
            <gz-policy-panel-exchange-user-groups-grid gz-form-input></gz-policy-panel-exchange-user-groups-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-labeled-content
            hasFormLabelWidth
            hasColon
            labelFontSize="${FontSizes.HEADING_6}"
            labelFontWeight="${FontWeights.NORMAL}"
            label="${translateFn('Recipients')}"
            class="mt-2"
          >
            <gz-form-field id="rule-scope-to-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeRuleScopeEmailSelection.ANY}
                @radioSelectionChanged=${this.handleScopeToEmailSelectionChange.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Any')}"
                  .value=${ExchangeRuleScopeEmailSelection.ANY}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('Specific')}"
                  .value=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </gz-labeled-content>
          <gz-text
            id="rule-scope-from-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'By default, the rule applies to all recipients. To apply different filtering settings and actions for certain sender groups, select {bold}Specific{/bold} and add a custom rule.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-scope-to-specific-field" class="mt-2 hidden">
            <gz-policy-panel-exchange-user-groups-grid gz-form-input></gz-policy-panel-exchange-user-groups-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="rule-scope-match-all-recipients-field" class="hidden pb-2">
            <gz-checkbox label="${translateFn('Match all recipients')}" gz-form-input></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule settings section -->
        <div id="rules-settings-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-2">${translateFn('Settings')}</gz-title>

          <gz-form-field id="rule-settings-detect-by-content-enabled-field">
            <gz-checkbox
              class="checkbox-subtitle"
              label="${translateFn('Detect by content type')}"
              gz-form-input
              checked
              @change="${this.onSettingDetectByContentSelectionChanged}"
            ></gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <div class="helper-text" id="rule-settings-detect-by-content-section">
            <gz-form-field id="rule-settings-detect-by-content-type-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeContentControlAttachmentFilteringRuleSettingsDetectType.ONLY_SELECTED_CATEGORIES}
                @radioSelectionChanged=${this.refreshSettings.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Only the following categories')}"
                  .value=${ExchangeContentControlAttachmentFilteringRuleSettingsDetectType.ONLY_SELECTED_CATEGORIES}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('All except the following categories')}"
                  .value=${ExchangeContentControlAttachmentFilteringRuleSettingsDetectType.ALL_EXCEPT_SELECTED_CATEGORIES}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>

            <div class="helper-text" id="rule-settings-detect-by-content-categories-section">
              <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize="${FontSizes.HEADING_6}"
                >${translateFn('Categories') + ':'}</gz-title
              >

              <div class="container">
                <div class="firstColumn">
                  <gz-form-field id="rule-settings-detect-by-content-category-executables-field">
                    <gz-checkbox label="${translateFn('Executable files')}" gz-form-input checked></gz-checkbox>
                  </gz-form-field>
                  <gz-form-field id="rule-settings-detect-by-content-category-images-field">
                    <gz-checkbox label="${translateFn('Images')}" gz-form-input></gz-checkbox>
                  </gz-form-field>
                  <gz-form-field id="rule-settings-detect-by-content-category-multimedia-field">
                    <gz-checkbox label="${translateFn('Multimedia')}" gz-form-input></gz-checkbox>
                  </gz-form-field>
                  <gz-form-field id="rule-settings-detect-by-content-category-archives-field">
                    <gz-checkbox
                      label="${translateFn('Archives')}"
                      gz-form-input
                      @change="${this.refreshSettings.bind(this)}"
                    ></gz-checkbox>
                  </gz-form-field>

                  <gz-form-field id="rule-settings-detect-by-content-custom-extensions-enabled-field">
                    <gz-checkbox
                      label="${translateFn('Custom extensions')}"
                      gz-form-input
                      @change="${this.onCustomExtensionsEnabledChanged}"
                    ></gz-checkbox>
                  </gz-form-field>
                  <gz-form-field
                    class="label-mw-250 hidden"
                    id="rule-settings-detect-by-content-custom-extensions-field"
                    disabled
                  >
                    <gz-chips-generator-input
                      gz-form-input
                      validationRegex="${EXTENSION_VALIDATION_REGEX}"
                      .strings=${{
                        placeholder: translateFn('Specify extensions here'),
                        hintText: translateFn('Use a comma to separate multiple extensions.'),
                        notUniqueErrorMessage: translateFn('The entry already exists.'),
                        invalidItemErrorMessage: translateFn('Invalid extension'),
                      }}
                    >
                    </gz-chips-generator-input>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>

                  <gz-form-field id="rule-settings-detect-by-content-true-file-type-detection-field">
                    <gz-checkbox label="${translateFn('Enable true file type detection')}" gz-form-input></gz-checkbox>
                    <gz-text fontSize="${FontSizes.PARAGRAPH_12}" class="helper-text"
                      >${translateFn(
                        'Select this option to enable signature-based file type detection, which prevents bypassing of attachment filtering by altering file extensions. File type detection may be resource-intensive.'
                      )}</gz-text
                    >
                  </gz-form-field>
                </div>

                <div class="secondColumn">
                  <gz-form-field id="rule-settings-detect-by-content-category-spreadsheets-field">
                    <gz-checkbox label="${translateFn('Spreadsheets')}" gz-form-input></gz-checkbox>
                  </gz-form-field>
                  <gz-form-field id="rule-settings-detect-by-content-category-presentations-field">
                    <gz-checkbox label="${translateFn('Presentations')}" gz-form-input></gz-checkbox>
                  </gz-form-field>
                  <gz-form-field id="rule-settings-detect-by-content-category-documents-field">
                    <gz-checkbox label="${translateFn('Documents')}" gz-form-input></gz-checkbox>
                  </gz-form-field>
                </div>
              </div>
            </div>
          </div>

          <!-- Rule settings: Archive scan -->
          <div id="rule-settings-archive-scan-section">
            <gz-form-field id="rule-settings-archive-scan-enabled-field">
              <gz-checkbox
                label="${translateFn('Scan inside archives')}"
                gz-form-input
                @change="${e => {
                  toggleDisabled(this.ruleSettingsArchiveScanMaximumDepthEnabledField, !e.detail.checked);
                  toggleDisabled(
                    this.ruleSettingsArchiveScanMaximumDepthField,
                    !e.detail.checked || !this.ruleSettingsArchiveScanMaximumDepthEnabledField.value
                  );
                }}"
              ></gz-checkbox>
            </gz-form-field>
            <div class="checkbox-with-input helper-text" id="rule-settings-archive-scan-maximum-depth-section">
              <gz-form-field
                class="checkbox-width disabled"
                id="rule-settings-archive-scan-maximum-depth-enabled-field"
              >
                <gz-checkbox
                  label="${translateFn('Archive maximum depth (levels)') + ':'}"
                  gz-form-input
                  @change="${e => toggleDisabled(this.ruleSettingsArchiveScanMaximumDepthField, !e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-settings-archive-scan-maximum-depth-field" class="disabled">
                <gz-input-number gz-form-input .min=${1} .max=${255} .value=${4}></gz-input-number>
              </gz-form-field>
            </div>
          </div>

          <gz-form-field id="rule-settings-detect-by-filename-enabled-field">
            <gz-checkbox
              class="checkbox-subtitle"
              label="${translateFn('Detect by filename')}"
              gz-form-input
              @change="${this.onSettingDetectByFilenameSelectionChanged}"
            ></gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field class="label-mw-250 hidden" id="rule-settings-detect-by-filename-patterns-field" disabled>
            <gz-chips-generator-input
              gz-form-input
              .strings=${{
                placeholder: translateFn('Enter extensions'),
                hintText: translateFn('Use a comma to separate multiple extensions.'),
                notUniqueErrorMessage: translateFn('The entry already exists.'),
              }}
            >
            </gz-chips-generator-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>

        <!-- Rule actions section -->
        <div id="rules-actions-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Actions')}</gz-title>

          <gz-text fontWeight="${FontWeights.BOLD}" fontSize="${FontSizes.HEADING_6}" class="action-title mt-3"
            >${translateFn('Primary action')}</gz-text
          >

          <!-- Rule actions section: Primary action -->
          <div id="rule-actions-primary-section" class="mt-2">
            <gz-form-field id="rule-actions-type-field">
              <gz-dropdown
                gz-form-input
                label="${translateFn('Action')}"
                .options=${getExchangeContentControlActionAttachmentRuleFormOptions()}
                @optionSelected="${this.handleActionsTypeFieldChange}"
              ></gz-dropdown>
            </gz-form-field>

            <gz-form-field id="rule-actions-deliver-to-field" class="hidden" disabled>
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.LARGE}"
                placeholder="${translateFn('Enter the mail address')}"
              ></gz-input>
            </gz-form-field>
          </div>

          <div id="rule-actions-secondary-section">
            <gz-text fontWeight="${FontWeights.BOLD}" fontSize="${FontSizes.HEADING_6}"
              >${translateFn('Secondary actions')}</gz-text
            >

            <!-- Rule actions section: Tag the email subject as -->
            <div class="checkbox-with-input" class="mt-2">
              <gz-form-field class="checkbox-width-220" id="rule-actions-modify-subject-field">
                <gz-checkbox
                  label="${translateFn('Tag the email subject as')}"
                  gz-form-input
                  @change="${e => this.toggleDisabledField(this.ruleActionsModifySubjectTextField, !e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-actions-modify-subject-text-field" disabled>
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter a label')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Email header -->
            <div>
              <gz-form-field class="checkbox-width-220" id="rule-actions-append-header-field">
                <gz-checkbox
                  label="${translateFn('Add an email header')}"
                  gz-form-input
                  @change="${e => this.toggleActionsAppendHeaderFieldDisabledState(!e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field
                class="hidden"
                id="rule-actions-append-header-name-field"
                label="${translateFn('Header name')}"
                disabled
              >
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter a name')}"
                ></gz-input>
              </gz-form-field>

              <gz-form-field
                class="hidden"
                id="rule-actions-append-header-value-field"
                label="${translateFn('Header value')}"
                disabled
              >
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter a value')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Save email to disk -->
            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width-220" id="rule-actions-save-mail-to-disk-field">
                <gz-checkbox
                  label="${translateFn('Save email to disk')}"
                  gz-form-input
                  @change="${e => this.toggleDisabledField(this.ruleActionsSaveMailToDiskPathField, !e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-actions-save-mail-to-disk-path-field" disabled>
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter the full path')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Archive to account -->
            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width-220" id="rule-actions-archive-to-account-field">
                <gz-checkbox
                  label="${translateFn('Archive to account')}"
                  gz-form-input
                  @change="${e =>
                    this.toggleDisabledField(this.ruleActionsArchiveToAccountAddressField, !e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-actions-archive-to-account-address-field" disabled>
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter the email archive address')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Send a copy to -->
            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width-220 hidden" id="rule-actions-send-copy-to-field">
                <gz-checkbox
                  label="${translateFn('Send a copy to')}"
                  gz-form-input
                  @change="${e => this.toggleDisabledField(this.ruleActionsSendCopyToAddressField, !e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-actions-send-copy-to-address-field" class="hidden" disabled>
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.LARGE}"
                  placeholder="${translateFn('Enter the email address')}"
                ></gz-input>
              </gz-form-field>
            </div>

            <!-- Rule actions section: Notify section -->
            <div id="rules-actions-notify-section" class="hidden">
              <gz-form-field id="rule-actions-notify-recipient-field" disabled>
                <gz-checkbox label="${translateFn('Notify the recipient')}" gz-form-input></gz-checkbox>
              </gz-form-field>
              <gz-text
                id="rule-actions-notify-recipient-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText infoAction helper-text"
                >${translateFn(
                  'Send a notification to the intended recipients when an action is taken on an email. Emails are sent only to mailboxes on domains accepted by the company email server.'
                )}
              </gz-text>

              <gz-form-field id="rule-actions-notify-sender-field" disabled>
                <gz-checkbox label="${translateFn('Notify the sender')}" gz-form-input></gz-checkbox>
              </gz-form-field>
              <gz-text
                id="rule-actions-notify-sender-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText infoAction helper-text"
                >${translateFn(
                  'Send notification to the sender when an action is taken on an email. Emails are only sent to mailboxes that belong to a domain accepted by the company email server.'
                )}
              </gz-text>

              <div class="checkbox-with-input">
                <gz-form-field class="checkbox-width-220" id="rule-actions-notify-account-field" disabled>
                  <gz-checkbox
                    label="${translateFn('Notify the user')}"
                    gz-form-input
                    @change="${e =>
                      this.toggleDisabledField(this.ruleActionsNotifyAccountRecipientsField, !e.detail.checked)}"
                  ></gz-checkbox>
                </gz-form-field>

                <gz-form-field id="rule-actions-notify-account-recipients-field" disabled>
                  <gz-input
                    gz-form-input
                    inputWidth="${InputWidthValues.LARGE}"
                    placeholder="${translateFn('Enter the email address')}"
                  ></gz-input>
                </gz-form-field>
              </div>
              <gz-text
                id="rule-actions-notify-account-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText infoAction helper-text"
                >${translateFn('Send notification to the specified mailboxes when an action is taken on an email.')}
              </gz-text>
            </div>
          </div>

          <gz-form-field id="rule-actions-stop-if-matched-field">
            <gz-checkbox label="${translateFn('Stop processing more rules')}" gz-form-input checked></gz-checkbox>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  /**
   * Handles the change event of the custom extensions enabled field.
   * @param event
   * @private
   */
  private onCustomExtensionsEnabledChanged(event: CustomEvent) {
    const isEnabled = event.detail.checked;

    // show/hide custom extensions section
    toggleVisibility(this.ruleSettingsDetectByContentCustomExtensionsField, isEnabled);
    this.toggleDisabledField(this.ruleSettingsDetectByContentCustomExtensionsField, !isEnabled);
  }

  /**
   * Handles the change event of the scope from email selection field.
   * @param event
   * @private
   */
  private handleScopeFromEmailSelectionChange(event: CustomEvent) {
    const isSpecific = event.detail.selectedValue === ExchangeRuleScopeEmailSelection.SPECIFIC;

    // show/hide user groups grid
    toggleVisibility(this.ruleScopeFromSpecificField, isSpecific);
    this.ruleScopeFromSpecificField.Validators = isSpecific ? ruleScopeSpecificValidators() : [];
  }

  /**
   * Handles the change event of the scope to email selection field.
   * @param event
   * @private
   */
  private handleScopeToEmailSelectionChange(event: CustomEvent) {
    const isSpecific = event.detail.selectedValue === ExchangeRuleScopeEmailSelection.SPECIFIC;

    // show/hide user groups grid
    toggleVisibility(this.ruleScopeToSpecificField, isSpecific);
    this.ruleScopeToSpecificField.Validators = isSpecific ? ruleScopeSpecificValidators() : [];

    // show/hide match all recipients
    toggleVisibility(this.ruleScopeMatchAllRecipientsField, isSpecific);
  }

  /**
   * Handles the change event of the actions type field.
   * @param event
   * @private
   */
  private handleActionsTypeFieldChange(event: CustomEvent) {
    // skip if the value is the same
    if (event.detail.oldValue?.value === event.detail.newValue.value) {
      return;
    }

    // for the first change, values from backend are used for secondary actions
    if (!this.ruleActionsTypeField.hasAttribute('no-impact')) {
      this.ruleActionsDeliverToField.value = '';
      this.ruleActionsModifySubjectField.value = false;
      this.ruleActionsModifySubjectTextField.value = '';
      this.ruleActionsAppendHeaderField.value = false;
      this.ruleActionsAppendHeaderNameField.value = '';
      this.ruleActionsAppendHeaderValueField.value = '';
      this.ruleActionsSaveMailToDiskField.value = false;
      this.ruleActionsSaveMailToDiskPathField.value = '';
      this.ruleActionsArchiveToAccountField.value = false;
      this.ruleActionsArchiveToAccountAddressField.value = '';
      this.ruleActionsSendCopyToField.value = false;
      this.ruleActionsSendCopyToAddressField.value = '';
      this.ruleActionsNotifyRecipientField.value = false;
      this.ruleActionsNotifySenderField.value = false;
      this.ruleActionsNotifyAccountField.value = false;
      this.ruleActionsNotifyAccountRecipientsField.value = '';
    } else {
      this.ruleActionsTypeField.removeAttribute('no-impact');
    }

    // show/hide deliver to field & disable it
    const toggleDeliverToVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsDeliverToField, isVisible);
      this.toggleDisabledField(this.ruleActionsDeliverToField, !isVisible);
    };
    // show/hide modify subject fields & disable it
    const toggleModifySubjectVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsModifySubjectField, isVisible);
      this.ruleActionsModifySubjectField.disabled = !isVisible;
      toggleVisibility(this.ruleActionsModifySubjectTextField, isVisible);
      this.toggleDisabledField(
        this.ruleActionsModifySubjectTextField,
        !isVisible || !this.ruleActionsModifySubjectField.value
      );
    };
    // show/hide archive to account fields & disable it
    const toggleArchiveToAccountVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsArchiveToAccountField, isVisible);
      this.ruleActionsArchiveToAccountField.disabled = !isVisible;
      toggleVisibility(this.ruleActionsArchiveToAccountAddressField, isVisible);
      this.toggleDisabledField(
        this.ruleActionsArchiveToAccountAddressField,
        !isVisible || !this.ruleActionsArchiveToAccountField.value
      );
    };
    // show/hide send copy to fields & disable it
    const toggleSendCopyToVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsSendCopyToField, isVisible);
      this.ruleActionsSendCopyToField.disabled = !isVisible;
      toggleVisibility(this.ruleActionsSendCopyToAddressField, isVisible);
      this.toggleDisabledField(
        this.ruleActionsSendCopyToAddressField,
        !isVisible || !this.ruleActionsSendCopyToField.value
      );
    };
    // show/hide append header fields & disable it
    const toggleAppendHeaderFieldVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsAppendHeaderField, isVisible);
      this.ruleActionsAppendHeaderField.disabled = !isVisible;
      this.toggleActionsAppendHeaderFieldDisabledState(!isVisible || !this.ruleActionsAppendHeaderField.value);
    };
    // show notify section
    const toggleNotifySectionVisibility = (isVisible: boolean) => {
      toggleVisibility(this.ruleActionsNotifySection, isVisible);
      this.ruleActionsNotifyRecipientField.disabled = !isVisible;
      this.ruleActionsNotifySenderField.disabled = !isVisible;
      this.ruleActionsNotifyAccountField.disabled = !isVisible;
      this.toggleDisabledField(
        this.ruleActionsNotifyAccountRecipientsField,
        !isVisible || !this.ruleActionsNotifyAccountField.value
      );
    };

    switch (event.detail.newValue.value) {
      case ExchangeContentControlRuleActionType.MOVE_TO_QUARANTINE:
      case ExchangeContentControlRuleActionType.REJECT_EMAIL:
        toggleDeliverToVisibility(false);
        toggleModifySubjectVisibility(false);
        toggleArchiveToAccountVisibility(false);
        toggleAppendHeaderFieldVisibility(false);
        toggleNotifySectionVisibility(true);
        break;
      case ExchangeContentControlRuleActionType.REDIRECT:
        toggleDeliverToVisibility(true);
        toggleModifySubjectVisibility(true);
        toggleArchiveToAccountVisibility(true);
        toggleAppendHeaderFieldVisibility(true);
        toggleNotifySectionVisibility(true);
        break;
      default:
        toggleDeliverToVisibility(false);
        toggleModifySubjectVisibility(true);
        toggleArchiveToAccountVisibility(true);
        toggleAppendHeaderFieldVisibility(true);
        toggleNotifySectionVisibility(false);
        break;
    }

    const actionsWithSendCopyToVisible = [
      ExchangeContentControlRuleActionType.REPLACE_ATTACHMENT,
      ExchangeContentControlRuleActionType.DELETE_ATTACHMENT,
      ExchangeContentControlRuleActionType.REJECT_EMAIL,
    ];
    const isSendCopyToVisible = actionsWithSendCopyToVisible.includes(event.detail.newValue.value);
    toggleSendCopyToVisibility(isSendCopyToVisible);
  }

  /**
   * Toggle disabled state of the append header fields.
   * @param disabled
   * @private
   */
  private toggleActionsAppendHeaderFieldDisabledState(disabled: boolean) {
    this.toggleDisabledField(this.ruleActionsAppendHeaderNameField, disabled);
    this.toggleDisabledField(this.ruleActionsAppendHeaderValueField, disabled);
    toggleVisibility(this.ruleActionsAppendHeaderNameField, !disabled);
    toggleVisibility(this.ruleActionsAppendHeaderValueField, !disabled);
  }

  /**
   * Toggle disabled state of the field.
   * @param field
   * @param disabled
   * @private
   */
  private toggleDisabledField(field: GzFormFieldWebComponent, disabled: boolean) {
    field.disabled = disabled;

    if (disabled) {
      field.resetField();
    } else {
      field.validate(true);
    }
  }

  /**
   * Validate the other field & refresh settings.
   * @private
   */
  private onSettingDetectByContentSelectionChanged() {
    // validate filter by filename
    this.ruleSettingsDetectByFilenameEnabledField?.validate(true);

    // refresh settings
    this.refreshSettings();
  }

  /**
   * Validate the other field. Show/hide patterns section.
   * @private
   */
  private onSettingDetectByFilenameSelectionChanged(event: CustomEvent) {
    const isEnabled = event.detail.checked;

    // show/hide patterns section
    toggleVisibility(this.ruleSettingsDetectByFilenamePatternsField, isEnabled);
    this.toggleDisabledField(this.ruleSettingsDetectByFilenamePatternsField, !isEnabled);

    // validate filter by content
    this.ruleSettingsDetectByContentEnabledField?.validate(true);
  }

  /**
   * Enable/Disable Archive Scan settings based on Detect by Content settings and Category archive value.
   * @private
   */
  private refreshSettings() {
    if (this.ruleSettingsDetectByContentEnabledField.value) {
      const hasOnlySelectedCategoriesWithArchives =
        this.ruleSettingsDetectByContentTypeField.value ===
          ExchangeContentControlAttachmentFilteringRuleSettingsDetectType.ONLY_SELECTED_CATEGORIES &&
        this.ruleSettingsDetectByContentCategoryArchivesField.value;
      const hasExceptCategoriesWithoutArchives =
        this.ruleSettingsDetectByContentTypeField.value ===
          ExchangeContentControlAttachmentFilteringRuleSettingsDetectType.ALL_EXCEPT_SELECTED_CATEGORIES &&
        !this.ruleSettingsDetectByContentCategoryArchivesField.value;

      if (hasOnlySelectedCategoriesWithArchives || hasExceptCategoriesWithoutArchives) {
        this.ruleSettingsArchiveScanEnabledField.value = false;
        toggleDisabled(this.ruleSettingsArchiveScanSection, true);
        return;
      }
    }

    toggleDisabled(this.ruleSettingsArchiveScanSection, false);
  }
}
